import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { MaskedLine, FadeUp, Page, Meta } from "../components/Reveal";
import { getProjects } from "../lib/api";
import { useContent } from "../lib/ContentContext";

function ProjectSection({ p, i }) {
  const imageLeft = i % 2 === 0;
  // Les blocs laissés « À compléter » dans l'admin restent invisibles publiquement.
  const visibleBlocks = (p.blocks || []).filter(
    (b) => b.texte && b.texte.trim() && b.texte.trim() !== "À compléter"
  );
  const externalCta = p.cta_url && !p.cta_url.startsWith("/");
  return (
    <section id={p.slug} data-testid={`projet-${p.slug}-section`}
      className={`${imageLeft ? "" : "bg-sage/30"} py-24 md:py-32`}>
      <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 md:px-12 lg:grid-cols-12">
        <FadeUp y={40} className={`lg:col-span-6 ${imageLeft ? "" : "lg:order-2"}`}>
          <div className="relative">
            <div className={`absolute -bottom-6 ${imageLeft ? "-right-6" : "-left-6"} h-full w-full rounded-[1.75rem] ${imageLeft ? "bg-sage/40" : "bg-cream"}`} aria-hidden="true" />
            <img src={p.img} alt={p.titre} data-testid={`projet-${p.slug}-img`}
              className="relative w-full rounded-[1.75rem] object-cover" />
          </div>
        </FadeUp>
        <div className={`lg:col-span-6 ${imageLeft ? "" : "lg:order-1"}`}>
          <FadeUp>
            <div className="flex flex-wrap items-center gap-4">
              {p.tags && (
                <p className="text-xs font-medium uppercase tracking-[0.25em] text-clay">{p.tags}</p>
              )}
              {p.badge && (
                <span data-testid={`projet-${p.slug}-badge`}
                  className="inline-flex items-center gap-2 rounded-full bg-clay/10 px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-clay">
                  <Sparkles className="h-3 w-3" />
                  {p.badge}
                </span>
              )}
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light tracking-tight md:text-5xl">{p.titre}</h2>
            {p.soustitre && (
              <p className="mt-4 max-w-lg font-serif text-xl font-light italic leading-snug text-moss md:text-2xl">
                {p.soustitre}
              </p>
            )}
          </FadeUp>
          {visibleBlocks.length > 0 && (
            <div className="mt-10 space-y-8">
              {visibleBlocks.map((b, bi) => (
                <FadeUp key={b.titre} delay={0.12 + 0.08 * bi}>
                  <div className="border-t border-charcoal/10 pt-6">
                    <h3 className="font-serif text-xl font-light italic text-clay md:text-2xl">{b.titre}</h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-smoke md:text-base">{b.texte}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          )}
          {(p.cta_url || p.cta_titre) && (
            <FadeUp delay={0.4}>
              {externalCta ? (
                <a href={p.cta_url} target="_blank" rel="noopener noreferrer" data-testid={`projet-${p.slug}-cta`}
                  className="group mt-10 inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
                  {p.cta_titre || "Découvrir"}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ) : (
                <Link to={p.cta_url || "/contact"} data-testid={`projet-${p.slug}-cta`}
                  className="group mt-10 inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
                  {p.cta_titre}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              )}
            </FadeUp>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Projets() {
  const c = useContent();
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    getProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  return (
    <Page testId="projets-page">
      <section className="mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-48">
        <FadeUp><Meta>{c("projets.hero.meta")}</Meta></FadeUp>
        <h1 className="mt-6 font-serif text-5xl font-light leading-[1.08] tracking-tight md:text-7xl">
          <MaskedLine delay={0.2}>{c("projets.hero.l1")}</MaskedLine>
          <MaskedLine delay={0.35} className="italic text-clay">{c("projets.hero.l2")}</MaskedLine>
        </h1>
        <FadeUp delay={0.6} className="mt-10 max-w-2xl">
          <p className="text-base leading-relaxed text-smoke md:text-lg">
            {c("projets.hero.p")}
          </p>
        </FadeUp>
      </section>

      {projects === null ? (
        <div className="flex justify-center py-24" data-testid="projects-loading">
          <Loader2 className="h-6 w-6 animate-spin text-clay" />
        </div>
      ) : projects.length === 0 ? (
        <p data-testid="projects-empty" className="mx-auto max-w-7xl px-6 py-16 text-center font-serif text-xl font-light italic text-smoke md:px-12">
          Les projets se préparent en coulisses.
        </p>
      ) : (
        projects.map((p, i) => <ProjectSection key={p.id} p={p} i={i} />)
      )}

      <section className="mx-auto max-w-7xl px-6 py-24 text-center md:px-12">
        <FadeUp>
          <p className="mx-auto max-w-3xl font-serif text-2xl font-light italic leading-snug tracking-tight text-charcoal md:text-4xl">
            {c("projets.fin.citation")}
          </p>
        </FadeUp>
      </section>
    </Page>
  );
}
